﻿import React, { useCallback, useEffect, useState } from 'react';

import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { Modal } from 'components/modal_manager';
import { FormattedMessage } from 'react-intl';
import { Col } from 'react-bootstrap';
import { useStableId } from 'hooks';

import 'components/GMetrixDialog/gmetrix-dialog.scss';

interface GMetrixDialogProps {
    actionButtonClick?: any;
    actionButtonDisabled: boolean;
    actionButtonText?: any;
    checkBoxLabel?: string;
    checkBoxValue?: boolean;
    children?: any;
    closeButtonText?: string;
    dialogLabel?: string | React.ReactNode;
    disabled?: boolean;
    invertHeaderFooterBackground?: boolean;
    manualHeight?: string;
    onCancel?: () => void;
    onClose?: () => void;
    setCheckBoxValue?: React.Dispatch<React.SetStateAction<boolean>>;
    show: boolean;
    showCancelButton?: boolean;
    showCheckBox?: boolean;
    size?: 'lg' | 'xl' | 'sm' | undefined;
}

export function GMetrixDialog({
    actionButtonClick,
    actionButtonDisabled,
    actionButtonText,
    checkBoxLabel,
    checkBoxValue,
    children,
    closeButtonText,
    dialogLabel = 'Dialog Label',
    disabled = false,
    onCancel,
    onClose,
    setCheckBoxValue,
    show,
    showCancelButton = true,
    showCheckBox = false,
    size = 'xl'
}: GMetrixDialogProps) {

    const [windowDemension, setWindowDemension] = useState({
        winHeight: window.innerHeight,
        winWidth: window.innerWidth
    });
    const titleId = useStableId('gmx-dialog-title');

    const allowForEscKeyToCloseModal = useCallback(event => {

        if (event.key === 'Escape') {

            //if esc key was not pressed in combination with ctrl or alt or shift
            const isNotCombinedKey = !(event.ctrlKey || event.altKey || event.shiftKey);
            if (isNotCombinedKey) {

                onCancel && onCancel();

            }

        }

    }, []);

    useEffect(() => {

        if (show) {

            document.addEventListener('keydown', allowForEscKeyToCloseModal);
            document.getElementById('content')?.classList.add('no-scroll');
            return () => {

                document.getElementById('content')?.classList.remove('no-scroll');

            };

        } else {

            document.removeEventListener('keydown', allowForEscKeyToCloseModal);

        }

    }, [show]);

    const detectSize = useCallback(() => {

        setWindowDemension({
            winHeight: window.innerHeight,
            winWidth: window.innerWidth
        });

    }, []);

    useEffect(() => {

        window.addEventListener('resize', detectSize);
        return () => {

            window.removeEventListener('resize', detectSize);

        };

    }, [windowDemension]);

    return (
        <>

            <Modal
                aria-labelledby={dialogLabel ? titleId : undefined}
                onHide={onClose ?? onCancel}
                show={show}
                size={size}
            >
                {dialogLabel && (
                    <Modal.Header
                        closeButton
                    >
                        <Modal.Title className="w-100" id={titleId}>
                            {dialogLabel}
                        </Modal.Title>
                    </Modal.Header>
                )}
                <Modal.Body >
                    {children}
                </Modal.Body>

                <Modal.Footer
                    className="border-0"
                >
                    <div
                        className={`d-md-flex align-items-center ${!showCancelButton ? 'justify-content-end' : 'justify-content-between'
                            }`}
                    >
                        {showCancelButton && (
                            <Col
                                className="col-12 mx-xl-2 mx-lg-2 mx-sm-0 mx-xs-0 my-2 d-flex justify-content-start"
                                lg={2}
                                md={3}
                                sm={12}
                                xl={2}
                            >
                                <GMetrixButton
                                    className="d-print-none"
                                    disabled={disabled}
                                    onClick={onCancel}
                                    variant="outline-secondary"
                                >
                                    {!closeButtonText ? <FormattedMessage defaultMessage="Cancel" id="cancel.89a86adc" /> : closeButtonText}
                                </GMetrixButton>
                            </Col>
                        )}
                        {showCheckBox && (
                            <Col className="ms-2">
                                <GMetrixCheckInput
                                    checked={checkBoxValue}
                                    label={checkBoxLabel as string}
                                    onChange={e => setCheckBoxValue && setCheckBoxValue(e.target.checked)} type={'switch'}
                                />
                            </Col>
                        )}
                        <Col
                            className="col-12 mx-xl-2 mx-lg-2 mx-sm-0 mx-xs-0 my-2 d-flex justify-content-end"
                            lg={2}
                            md={3}
                            sm={12}
                            xl={2}
                            xs={12}
                        >
                            <GMetrixButton
                                className="d-print-none"
                                disabled={actionButtonDisabled}
                                onClick={actionButtonClick}
                                variant="primary"
                            >
                                {actionButtonText}
                            </GMetrixButton>
                        </Col>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );

}
